import * as Sentry from '@sentry/browser';
import axios from 'axios';
import moment from 'moment';

import { userSelector } from '../reducers/current-user-selectors';
import { StoreManager } from '../reducers/store-manager';

import { post } from '../api/api';

import { isProd } from '../config/config';
import {
  DEV_DEFAULT_FEATURE_GATE_ENABLED,
  ENABLE_PRODUCT_TELEMETRY,
} from '../config/feature-config';
import { getParams } from '../widgets/browser-history-spy';

export type ProductTelemetryEvent = {
  event: string;
  value?: number;
  metadata?: Record<string, string>;
};

const apiHost = () => {
  if (isProd) {
    return 'https://metrics.teamengine.io';
  }
  // Use "localhost" on the client side and "metricservice" on the server
  // This is because the client runs outside of Docker so needs to access it
  // via the url on the host.
  return 'http://localhost:3391';
};

/**
 * Takes our user model and maps it to a more external-service-friendly model,
 * importantly keeping the attributes flat (no more than one level of nesting)
 * and providing defaults as necessary (like adding empty strings so that keys
 * can be checked for being missing)
 *
 * NOTE: This function is located in this file for a specific reason - it needs to
 * be kept in sync with the `product-telemetry-controller`'s version on the
 * server.
 *
 * @param currentUser - the user that is returned from the registerUser action
 * @returns A flat user object that can be used in a more reliable way with
 * external services
 */
export const normalizeUserAttributes = (currentUser: AuthenticatedUser) =>
  ({
    username: currentUser.username,
    name: `${currentUser.firstName} ${currentUser.lastName}`,
    email: currentUser.email,
    accountId: currentUser.accountId ? String(currentUser.accountId) : '',
    accountName: currentUser.account?.accountName ?? '',
    accountGroupId: currentUser.accountGroupId ? String(currentUser.accountGroupId) : '',
    accountGroupName: currentUser.AccountGroup?.name ?? '',
    accountStatus: currentUser.account?.status ?? '',
    isTeamEngineAdmin: String(currentUser.isAdmin),
    isAccountAdmin: String(currentUser.isAccountAdmin),
    hasMergeIntegration: String(currentUser.hasMergeIntegration),
  } as const);

/**
 * The accountId we use to identify the user is a little tricky - most users will have a standard
 * number on their user for accountId. We also have users that are apart of an account group, and
 * so our accountId here changes to include that. Lastly, if you are a team engine admin, we report
 * that as "admin". The reason that this is important is to enable us to do things like turning on
 * a feature flag for an entire account
 *
 * NOTE: This function should be kept in sync with the `product-telemetry-controller`'s version on the
 * server.
 *
 * @param currentUser - the user that is returned from the registerUser action
 * @returns A string representing both he type of account in the prefix ('admin', 'accountId', or 'accountGroup').
 * and an appropriate number (user.accountId or user.accountGroupId)
 */
const getStatSigAccountId = (currentUser: AuthenticatedUser) =>
  currentUser.isAdmin
    ? 'admin'
    : currentUser.accountId
    ? `accountId-${currentUser.accountId}`
    : currentUser.accountGroupId
    ? `accountGroupId-${currentUser.accountGroupId}`
    : '';

/**
 * Maps our user model to our Metric's Service User Model, found here:
 * https://github.com/TeamEngineIO/metrics-service/blob/main/src/utils/types/collect-metric-request-user.ts
 *
 * The identifiers and metadata objects ae broadly spread to the resulting metric collection services, so
 * can contain arbitrary key/value pairs
 *
 * NOTE: This function should be kept in sync with the `product-telemetry-controller`'s version on the
 * server.
 *
 * @param currentUser - the user that is returned from the registerUser action
 * @returns A user that can be reported to the metric service
 */
const getStatSigUser = (currentUser: AuthenticatedUser) => ({
  username: currentUser.username,
  firstName: currentUser.firstName,
  lastName: currentUser.lastName,
  email: currentUser.email,
  identifiers: {
    // Duplicate the identifier here so that our metrics service always uses it as an identifier
    // Theoretically, there are situations where username !== email (for example, perhaps automated
    // user event collection), so we leave the API flexible and duplicate here
    email: currentUser.email,
    accountId: getStatSigAccountId(currentUser),
  },
  metadata: {
    ...normalizeUserAttributes(currentUser),
    userCreationDate: currentUser.createdAt ? moment(currentUser.createdAt).format() : undefined,
  },
});

/**
 * NOTE: This class is basically duplicated between our client and server, and in particular
 * the data captured in the methods above should be kept in sync. There are important differences
 * in how the authentication is handled between the client and server, so unfortunately the code
 * can not be literally reused. For scale purposes, it's also good to separate
 *
 * This class is meant to abstract the collection of events away from the rest of the app.
 * Technically, it needs to be used with an authenticated user because we're using the
 * API.post method under the hood, and we'd need to do something else if we wanted to collect
 * events from anonymous users.
 */
export class ProductTelemetryController {
  /**
   * Collects events by sending them to the metrics service. The params of
   * the path will be automatically passed with the event as to capture things like orgId and
   * jobId automatically
   */
  static logEvent = (
    name: string,
    options?: { value?: number | string; metadata?: Record<string, string> },
  ) => {
    if (!ENABLE_PRODUCT_TELEMETRY) {
      return;
    }
    const hostToUse = apiHost();

    // A little hacky here, but it's useful to be able to call this controller from anywhere in
    // the app, not only in components. So we use this method of access the store
    const state = StoreManager.getStore().getState();
    const currentUser = userSelector(state);

    const user = getStatSigUser(currentUser);

    // On the server, authentication is handled in a more explicit way, as it's a service-to-service
    // call. This request will hit the same metrics service, but utilize our Auth0-based authentication

    const params = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    axios
      .post(
        `${hostToUse}/enqueue`,
        {
          user,
          event: {
            name,
            value: options?.value,
            metadata: {
              ...(options?.metadata ? options.metadata : {}),
              // By default, include the path parameters, which by default get us things like orgId
              // and jobId without the caller needing to pass them
              ...getParams(),
              // Also add the current URL to the event
              currentPage: window.location.href,
            },
            userAgent: navigator.userAgent,
          },
        },
        params,
      )
      // Swallow any errors here so that the client isn't interrupted by a failure to collect metrics
      .catch((e) => Sentry.captureException(e));
  };

  static checkGate = async (currentUser: AuthenticatedUser, gateName: string) => {
    if (DEV_DEFAULT_FEATURE_GATE_ENABLED !== null) {
      // We return a fake response since this is called from a dispatch action
      return {
        status: 200,
        ok: true,
        json: async () => ({
          isGateEnabled: DEV_DEFAULT_FEATURE_GATE_ENABLED,
        }),
      } as unknown as Response;
    }

    const hostToUse = apiHost();
    const user = getStatSigUser(currentUser);

    const params = {
      apiHost: hostToUse,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user, gateName }),
    };

    return post('/check-gate', params);
  };

  static bustFeatureGateCache = async (username?: string) => {
    if (DEV_DEFAULT_FEATURE_GATE_ENABLED !== null) {
      return { ok: true };
    }

    const hostToUse = apiHost();

    const params = {
      apiHost: hostToUse,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username }),
    };

    return post('/bust-feature-gate-cache', params);
  };
}
